
import { useEffect, useMemo, useState } from "react";
import {
    FaCog,
    FaBolt,
    FaBatteryFull,
    FaPlug,
    FaThermometerHalf,
    FaSave,
    FaSyncAlt,
    FaCheckCircle,
    FaExclamationCircle,
    FaChevronDown
} from "react-icons/fa";

import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api from "../services/api"; // adjust path to wherever api.js actually lives
import "../styles/settings.css";

const API_BASE =
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    "";

const defaultSettings = {
    // General
    outputVoltage: 230,
    outputFrequency: 50,
    outputPriority: "SBU",
    gridWorkingRange: "UPS",

    // Charging
    chargerPriority: "Solar First",
    batteryType: "User",
    maxChargingCurrent: 0,
    maxUtilityChargingCurrent: 0,

    // Battery
    batteryRechargeVoltage: 0,
    batteryRedischargeVoltage: 0,
    batteryCutoffVoltage: 0,
    batteryBulkVoltage: 0,
    batteryFloatVoltage: 0,

    // System
    overloadRestart: false,
    overTemperatureRestart: false,
    backlight: true,
    buzzer: true,
    powerSaving: false,
    lcdTimeout: true,
    sourceInterruptAlarm: true,
    faultCodeRecording: true,
    overloadBypass: false,

    // Advanced
    pvOKCondition: "",
    pvPowerBalance: "",
    outputMode: ""
};

const settingDefinitions = {
    outputVoltage: {
        label: "Output Voltage",
        unit: "V"
    },
    outputFrequency: {
        label: "Output Frequency",
        unit: "Hz"
    },
    outputPriority: {
        label: "Output Priority"
    },
    gridWorkingRange: {
        label: "Grid Working Range"
    },
    chargerPriority: {
        label: "Charger Priority"
    },
    batteryType: {
        label: "Battery Type"
    },
    maxChargingCurrent: {
        label: "Maximum Charging Current",
        unit: "A"
    },
    maxUtilityChargingCurrent: {
        label: "Maximum Utility Charging Current",
        unit: "A"
    },
    batteryRechargeVoltage: {
        label: "Battery Re-charge Voltage",
        unit: "V"
    },
    batteryRedischargeVoltage: {
        label: "Battery Re-discharge Voltage",
        unit: "V"
    },
    batteryCutoffVoltage: {
        label: "Battery Cut-off Voltage",
        unit: "V"
    },
    batteryBulkVoltage: {
        label: "Bulk / CV Voltage",
        unit: "V"
    },
    batteryFloatVoltage: {
        label: "Float Voltage",
        unit: "V"
    },
    overloadRestart: {
        label: "Overload Restart"
    },
    overTemperatureRestart: {
        label: "Over-temperature Restart"
    },
    backlight: {
        label: "LCD Backlight"
    },
    buzzer: {
        label: "Buzzer"
    },
    powerSaving: {
        label: "Power Saving"
    },
    lcdTimeout: {
        label: "LCD Timeout"
    },
    sourceInterruptAlarm: {
        label: "Source Interrupt Alarm"
    },
    faultCodeRecording: {
        label: "Fault Code Recording"
    },
    overloadBypass: {
        label: "Overload Bypass"
    },
    pvOKCondition: {
        label: "PV OK Condition"
    },
    pvPowerBalance: {
        label: "PV Power Balance"
    },
    outputMode: {
        label: "Output Mode"
    }
};
function normalizeFlag(value, fallback) {
    if (value === undefined || value === null) {
        return fallback;
    }

    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        return value === 1;
    }

    if (typeof value === "string") {
        const v = value.trim().toLowerCase();

        if (v === "1" || v === "true" || v === "on") {
            return true;
        }

        if (v === "0" || v === "false" || v === "off") {
            return false;
        }
    }

    return fallback;
}

function normalizeSettings(data) {
    const source = data?.settings || data || {};

    return {
        ...defaultSettings,

        outputVoltage:
            Number(source.outputVoltage ?? source.acOutputVoltage ?? 230),

        outputFrequency:
            Number(source.outputFrequency ?? source.acOutputFrequency ?? 50),

        outputPriority:
            source.outputPriority ||
            source.outputSourcePriority ||
            "SBU",

        gridWorkingRange:
            source.gridWorkingRange ||
            source.inputVoltageRange ||
            "UPS",

        chargerPriority:
            source.chargerPriority ||
            source.chargerSourcePriority ||
            "Solar First",

        batteryType:
            source.batteryType || "User",

        maxChargingCurrent:
            Number(source.maxChargingCurrent ?? 0),

        maxUtilityChargingCurrent:
            Number(source.maxUtilityChargingCurrent ?? source.maxACChargingCurrent ?? 0),

        batteryRechargeVoltage:
            Number(source.batteryRechargeVoltage ?? 0),

        batteryRedischargeVoltage:
            Number(source.batteryRedischargeVoltage ?? 0),

        batteryCutoffVoltage:
            Number(
                source.batteryCutoffVoltage ??
                source.batteryUnderVoltage ??
                0
            ),

        batteryBulkVoltage:
            Number(source.batteryBulkVoltage ?? 0),

        batteryFloatVoltage:
            Number(source.batteryFloatVoltage ?? 0),

        overloadRestart: normalizeFlag(
    source.overloadRestart ??
    source.overload_restart ??
    source.flag_overload_restart,
    false
),

overTemperatureRestart: normalizeFlag(
    source.overTemperatureRestart ??
    source.over_temperature_restart ??
    source.flag_over_temp_restart,
    false
),

backlight: normalizeFlag(
    source.backlight ??
    source.flag_backlight,
    true
),

buzzer: normalizeFlag(
    source.buzzer ??
    source.flag_buzzer,
    true
),

powerSaving: normalizeFlag(
    source.powerSaving ??
    source.flag_power_saving,
    false
),

lcdTimeout: normalizeFlag(
    source.lcdTimeout ??
    source.flag_lcd_timeout,
    true
),

sourceInterruptAlarm: normalizeFlag(
    source.sourceInterruptAlarm ??
    source.source_interrupt_alarm ??
    source.flag_source_alarm,
    true
),

faultCodeRecording: normalizeFlag(
    source.faultCodeRecording ??
    source.fault_code_recording ??
    source.flag_fault_recording,
    true
),

overloadBypass: normalizeFlag(
    source.overloadBypass ??
    source.overload_bypass ??
    source.flag_overload_bypass,
    false
),

        pvOKCondition:
            source.pvOKCondition || "",

        pvPowerBalance:
            source.pvPowerBalance || "",

        outputMode:
            source.outputMode || ""
    };
}

function valuesEqual(a, b) {
    if (typeof a === "number" || typeof b === "number") {
        return Number(a) === Number(b);
    }

    return a === b;
}

function Toggle({ checked, onChange, disabled }) {
    return (
        <button
            type="button"
            className={`settingsToggle ${checked ? "active" : ""}`}
            onClick={() => onChange(!checked)}
            disabled={disabled}
            aria-pressed={checked}
        >
            <span className="toggleTrack">
                <span className="toggleThumb" />
            </span>

            <span className="toggleText">
                {checked ? "ON" : "OFF"}
            </span>
        </button>
    );
}

function SelectControl({
    value,
    onChange,
    options,
    disabled = false
}) {
    return (
        <div className="settingsSelectWrap">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
            >
                {options.map((option) => (
                    <option
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </option>
                ))}
            </select>

            <FaChevronDown className="selectChevron" />
        </div>
    );
}

function NumberControl({
    value,
    onChange,
    unit,
    step = "1",
    disabled = false
}) {
    return (
        <div className="numberControl">
            <input
                type="number"
                value={value}
                step={step}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
            />

            {unit && (
                <span className="numberUnit">
                    {unit}
                </span>
            )}
        </div>
    );
}

function SettingRow({
    label,
    description,
    children,
    changed
}) {
    return (
        <div className={`settingRow ${changed ? "changed" : ""}`}>
            <div className="settingInfo">
                <span className="settingLabel">
                    {label}
                </span>

                {description && (
                    <span className="settingDescription">
                        {description}
                    </span>
                )}
            </div>

            <div className="settingControl">
                {children}
            </div>
        </div>
    );
}

export default function Settings() {
    const [menuOpen, setMenuOpen] = useState(false);

    const [settings, setSettings] = useState(defaultSettings);
    const [originalSettings, setOriginalSettings] =
        useState(defaultSettings);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);

    const [message, setMessage] = useState(null);

    const [device, setDevice] = useState(null);

    /*
     * The application can expose the selected device through localStorage.
     * We support several common keys so this page does not depend on
     * one particular dashboard implementation.
     */
    useEffect(() => {
        try {
            const raw =
                localStorage.getItem("selectedDevice") ||
                localStorage.getItem("currentDevice") ||
                localStorage.getItem("device");

            if (raw) {
                setDevice(
                    raw.startsWith("{")
                        ? JSON.parse(raw)
                        : { serial_number: raw }
                );
            }
        } catch {
            // Ignore invalid localStorage data.
        }
    }, []);

    const serial = useMemo(() => {
    return (
        localStorage.getItem("activeDeviceSerial") ||
        device?.serial_number ||
        device?.serial ||
        ""
    );
}, [device]);

    const changedSettings = useMemo(() => {
        const changes = {};

        Object.keys(settings).forEach((key) => {
            if (!valuesEqual(settings[key], originalSettings[key])) {
                changes[key] = {
                    oldValue: originalSettings[key],
                    newValue: settings[key]
                };
            }
        });

        return changes;
    }, [settings, originalSettings]);

    const changedCount =
        Object.keys(changedSettings).length;

    async function loadSettings(showRefreshing = false) {
        if (showRefreshing) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        setMessage(null);

        try {
    const response = await api.get(
    `/devices/${encodeURIComponent(serial)}/settings`
);

    const normalized = normalizeSettings(response.data);

    setSettings(normalized);
    setOriginalSettings(normalized);

    setMessage({
        type: "ok",
        text: "Settings synchronized with inverter."
    });

} catch (error) {
    setMessage({
        type: "error",
        text:
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Unable to load inverter settings."
    });
} finally {
    setLoading(false);
    setRefreshing(false);
}
    }

    useEffect(() => {
    if (!serial) {
        setMessage({ type: "error", text: "No device selected." });
        setLoading(false);
        return;
    }
    loadSettings(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [serial]);

    function updateSetting(key, value) {
        setSettings((current) => ({
            ...current,
            [key]: value
        }));

        setMessage(null);
    }

    async function handleApplyChanges() {
    if (changedCount === 0) {
        setMessage({ type: "ok", text: "There are no changes to apply." });
        return;
    }

    setSaving(true);
    setMessage(null);

    // Only send the keys that actually changed
    const settingsToApply = {};
    Object.keys(changedSettings).forEach((key) => {
        settingsToApply[key] = settings[key];
    });

    try {
        const response = await api.post(
            `/devices/${encodeURIComponent(serial)}/settings`,
            { settings: settingsToApply }   // <-- diff only
        );

        const data = response.data || {};

const verifiedFromResults = Object.values(data.results || {}).reduce(
    (acc, result) => ({
        ...acc,
        ...(result?.settings || {})
    }),
    {}
);

const verifiedSource = {
    ...(data.verifiedSettings || {}),
    ...(data.settings || {}),
    ...verifiedFromResults
};

const finalSettings = normalizeSettings({
    ...settings,
    ...verifiedSource,
    ...settingsToApply
});

setSettings(finalSettings);
setOriginalSettings(finalSettings);

        setMessage({
            type: "ok",
            text: data.message || "Settings applied and verified successfully."
        });

    } catch (error) {
        setMessage({
            type: "error",
            text:
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                "Could not apply inverter settings."
        });
    } finally {
        setSaving(false);
    }
}

    function resetChanges() {
        setSettings(originalSettings);

        setMessage({
            type: "ok",
            text: "Unsaved changes discarded."
        });
    }

    return (
        <div className="layout">

            <Sidebar
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
            />

            <div className="content">

                <Topbar
                    setMenuOpen={setMenuOpen}
                    title="Inverter Settings"
                    subtitle="Configure inverter operating parameters"
                />

                <div className="settingsBody">

                    {/* ---------- Header ---------- */}

                    <div className="settingsPageHeader">

                        <div>
                            <h1>Inverter Settings</h1>

                            <p>
                                Configure and synchronize your inverter
                                remotely.
                            </p>
                        </div>

                        <div className="deviceIdentity">

                            <span className="deviceIdentityLabel">
                                DEVICE
                            </span>

                            <strong>
                                {serial}
                            </strong>

                        </div>

                    </div>

                    {/* ---------- Sync status ---------- */}

                    <div className="settingsToolbar">

                        <div className="syncStatus">

                            <span
                                className={`syncDot ${
                                    loading
                                        ? "loading"
                                        : message?.type === "error"
                                            ? "error"
                                            : "online"
                                }`}
                            />

                            <span>
                                {loading
                                    ? "Reading inverter settings..."
                                    : message?.type === "error"
                                        ? "Synchronization error"
                                        : "Settings synchronized"}
                            </span>

                        </div>

                        <button
                            type="button"
                            className="refreshButton"
                            onClick={() => loadSettings(true)}
                            disabled={refreshing || saving}
                        >
                            <FaSyncAlt
                                className={
                                    refreshing
                                        ? "spinIcon"
                                        : ""
                                }
                            />

                            {refreshing
                                ? "Refreshing..."
                                : "Refresh"}
                        </button>

                    </div>

                    {/* ---------- Message ---------- */}

                    {message && (
                        <div
                            className={`settingsMsg ${message.type}`}
                        >
                            {message.type === "ok" ? (
                                <FaCheckCircle />
                            ) : (
                                <FaExclamationCircle />
                            )}

                            <span>
                                {message.text}
                            </span>
                        </div>
                    )}

                    {loading ? (

                        <div className="settingsLoading">

                            <FaSyncAlt className="spinIcon" />

                            <span>
                                Reading current inverter configuration...
                            </span>

                        </div>

                    ) : (

                        <>

                            {/* ================= GENERAL ================= */}

                            <section className="settingsCard">

                                <div className="settingsCardHeader">

                                    <div className="sectionIcon">
                                        <FaCog />
                                    </div>

                                    <div>
                                        <h2>General</h2>
                                        <p>
                                            Basic AC output and operating
                                            preferences.
                                        </p>
                                    </div>

                                </div>

                                <div className="settingsRows">

                                    <SettingRow
                                        label="Output Voltage"
                                        description="Nominal inverter AC output voltage"
                                        changed={
                                            !!changedSettings.outputVoltage
                                        }
                                    >
                                        <SelectControl
                                            value={String(
                                                settings.outputVoltage
                                            )}
                                            onChange={(value) =>
                                                updateSetting(
                                                    "outputVoltage",
                                                    Number(value)
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "220",
                                                    label: "220 V"
                                                },
                                                {
                                                    value: "230",
                                                    label: "230 V"
                                                },
                                                {
                                                    value: "240",
                                                    label: "240 V"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Output Frequency"
                                        description="AC output frequency"
                                        changed={
                                            !!changedSettings.outputFrequency
                                        }
                                    >
                                        <SelectControl
                                            value={String(
                                                settings.outputFrequency
                                            )}
                                            onChange={(value) =>
                                                updateSetting(
                                                    "outputFrequency",
                                                    Number(value)
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "50",
                                                    label: "50 Hz"
                                                },
                                                {
                                                    value: "60",
                                                    label: "60 Hz"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Output Priority"
                                        description="Determines the preferred energy source"
                                        changed={
                                            !!changedSettings.outputPriority
                                        }
                                    >
                                        <SelectControl
                                            value={settings.outputPriority}
                                            onChange={(value) =>
                                                updateSetting(
                                                    "outputPriority",
                                                    value
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "Utility First",
                                                    label: "Utility First"
                                                },
                                                {
                                                    value: "Solar First",
                                                    label: "Solar First"
                                                },
                                                {
                                                    value: "SBU",
                                                    label: "SBU — Solar → Battery → Utility"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Grid Working Range"
                                        description="AC input operating range"
                                        changed={
                                            !!changedSettings.gridWorkingRange
                                        }
                                    >
                                        <SelectControl
                                            value={
                                                settings.gridWorkingRange
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "gridWorkingRange",
                                                    value
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "UPS",
                                                    label: "UPS"
                                                },
                                                {
                                                    value: "Appliance",
                                                    label: "Appliance"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                </div>

                            </section>

                            {/* ================= CHARGING ================= */}

                            <section className="settingsCard">

                                <div className="settingsCardHeader">

                                    <div className="sectionIcon">
                                        <FaBolt />
                                    </div>

                                    <div>
                                        <h2>Charging</h2>
                                        <p>
                                            Configure how the inverter charges
                                            the battery.
                                        </p>
                                    </div>

                                </div>

                                <div className="settingsRows">

                                    <SettingRow
                                        label="Charger Priority"
                                        description="Select the preferred charging source"
                                        changed={
                                            !!changedSettings.chargerPriority
                                        }
                                    >
                                        <SelectControl
                                            value={
                                                settings.chargerPriority
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "chargerPriority",
                                                    value
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "Utility First",
                                                    label: "Utility First"
                                                },
                                                {
                                                    value: "Solar First",
                                                    label: "Solar First"
                                                },
                                                {
                                                    value: "Solar + Utility",
                                                    label: "Solar + Utility"
                                                },
                                                {
                                                    value: "Solar Only",
                                                    label: "Solar Only"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Maximum Charging Current"
                                        description="Maximum combined charging current"
                                        changed={
                                            !!changedSettings.maxChargingCurrent
                                        }
                                    >
                                        <NumberControl
                                            value={
                                                settings.maxChargingCurrent
                                            }
                                            unit="A"
                                            onChange={(value) =>
                                                updateSetting(
                                                    "maxChargingCurrent",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Maximum Utility Charging Current"
                                        description="Maximum AC/grid charging current"
                                        changed={
                                            !!changedSettings.maxUtilityChargingCurrent
                                        }
                                    >
                                        <NumberControl
                                            value={
                                                settings.maxUtilityChargingCurrent
                                            }
                                            unit="A"
                                            onChange={(value) =>
                                                updateSetting(
                                                    "maxUtilityChargingCurrent",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </SettingRow>

                                </div>

                            </section>

                            {/* ================= BATTERY ================= */}

                            <section className="settingsCard">

                                <div className="settingsCardHeader">

                                    <div className="sectionIcon">
                                        <FaBatteryFull />
                                    </div>

                                    <div>
                                        <h2>Battery</h2>
                                        <p>
                                            Battery chemistry and charging
                                            voltage configuration.
                                        </p>
                                    </div>

                                </div>

                                <div className="settingsRows">

                                    <SettingRow
                                        label="Battery Type"
                                        description="Battery charging profile"
                                        changed={
                                            !!changedSettings.batteryType
                                        }
                                    >
                                        <SelectControl
                                            value={settings.batteryType}
                                            onChange={(value) =>
                                                updateSetting(
                                                    "batteryType",
                                                    value
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "AGM",
                                                    label: "AGM"
                                                },
                                                {
                                                    value: "Flooded",
                                                    label: "Flooded"
                                                },
                                                {
                                                    value: "User",
                                                    label: "User Defined"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Battery Re-charge Voltage"
                                        description="Voltage at which charging/grid return threshold is reached"
                                        changed={
                                            !!changedSettings.batteryRechargeVoltage
                                        }
                                    >
                                        <NumberControl
                                            value={
                                                settings.batteryRechargeVoltage
                                            }
                                            unit="V"
                                            step="0.1"
                                            onChange={(value) =>
                                                updateSetting(
                                                    "batteryRechargeVoltage",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Battery Re-discharge Voltage"
                                        description="Voltage threshold for returning to battery operation"
                                        changed={
                                            !!changedSettings.batteryRedischargeVoltage
                                        }
                                    >
                                        <NumberControl
                                            value={
                                                settings.batteryRedischargeVoltage
                                            }
                                            unit="V"
                                            step="0.1"
                                            onChange={(value) =>
                                                updateSetting(
                                                    "batteryRedischargeVoltage",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Battery Cut-off Voltage"
                                        description="Low-voltage battery protection threshold"
                                        changed={
                                            !!changedSettings.batteryCutoffVoltage
                                        }
                                    >
                                        <NumberControl
                                            value={
                                                settings.batteryCutoffVoltage
                                            }
                                            unit="V"
                                            step="0.1"
                                            onChange={(value) =>
                                                updateSetting(
                                                    "batteryCutoffVoltage",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Bulk / CV Voltage"
                                        description="Constant-voltage charging level"
                                        changed={
                                            !!changedSettings.batteryBulkVoltage
                                        }
                                    >
                                        <NumberControl
                                            value={
                                                settings.batteryBulkVoltage
                                            }
                                            unit="V"
                                            step="0.1"
                                            onChange={(value) =>
                                                updateSetting(
                                                    "batteryBulkVoltage",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Float Voltage"
                                        description="Battery float charging voltage"
                                        changed={
                                            !!changedSettings.batteryFloatVoltage
                                        }
                                    >
                                        <NumberControl
                                            value={
                                                settings.batteryFloatVoltage
                                            }
                                            unit="V"
                                            step="0.1"
                                            onChange={(value) =>
                                                updateSetting(
                                                    "batteryFloatVoltage",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </SettingRow>

                                </div>

                            </section>

                            {/* ================= SYSTEM ================= */}

                            <section className="settingsCard">

                                <div className="settingsCardHeader">

                                    <div className="sectionIcon">
                                        <FaPlug />
                                    </div>

                                    <div>
                                        <h2>System</h2>
                                        <p>
                                            Inverter alarms, restart and
                                            display behaviour.
                                        </p>
                                    </div>

                                </div>

                                <div className="settingsRows">

                                    <SettingRow
                                        label="Overload Restart"
                                        description="Automatically restart after an overload"
                                        changed={
                                            !!changedSettings.overloadRestart
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.overloadRestart
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "overloadRestart",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Over-temperature Restart"
                                        description="Automatically restart after an over-temperature event"
                                        changed={
                                            !!changedSettings.overTemperatureRestart
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.overTemperatureRestart
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "overTemperatureRestart",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="LCD Backlight"
                                        description="Keep the LCD backlight enabled"
                                        changed={
                                            !!changedSettings.backlight
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.backlight
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "backlight",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Buzzer"
                                        description="Enable audible inverter alarms"
                                        changed={
                                            !!changedSettings.buzzer
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.buzzer
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "buzzer",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Power Saving"
                                        description="Enable inverter power-saving mode"
                                        changed={
                                            !!changedSettings.powerSaving
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.powerSaving
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "powerSaving",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="LCD Timeout"
                                        description="Return LCD to the default page after inactivity"
                                        changed={
                                            !!changedSettings.lcdTimeout
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.lcdTimeout
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "lcdTimeout",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Source Interrupt Alarm"
                                        description="Alarm when the primary AC source is interrupted"
                                        changed={
                                            !!changedSettings.sourceInterruptAlarm
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.sourceInterruptAlarm
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "sourceInterruptAlarm",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Fault Code Recording"
                                        description="Store inverter fault codes"
                                        changed={
                                            !!changedSettings.faultCodeRecording
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.faultCodeRecording
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "faultCodeRecording",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Overload Bypass"
                                        description="Allow bypass operation during overload conditions"
                                        changed={
                                            !!changedSettings.overloadBypass
                                        }
                                    >
                                        <Toggle
                                            checked={
                                                settings.overloadBypass
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "overloadBypass",
                                                    value
                                                )
                                            }
                                        />
                                    </SettingRow>

                                </div>

                            </section>

                            {/* ================= ADVANCED ================= */}

                            <section className="settingsCard">

                                <div className="settingsCardHeader">

                                    <div className="sectionIcon">
                                        <FaCog />
                                    </div>

                                    <div>
                                        <h2>Advanced</h2>
                                        <p>
                                            Advanced inverter operating
                                            parameters.
                                        </p>
                                    </div>

                                </div>

                                <div className="settingsRows">

                                    <SettingRow
                                        label="PV OK Condition"
                                        description="Parallel-system PV availability logic"
                                        changed={
                                            !!changedSettings.pvOKCondition
                                        }
                                    >
                                        <SelectControl
                                            value={
                                                settings.pvOKCondition
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "pvOKCondition",
                                                    value
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "",
                                                    label: "Not available"
                                                },
                                                {
                                                    value: "0",
                                                    label: "Any inverter PV available"
                                                },
                                                {
                                                    value: "1",
                                                    label: "All inverters PV available"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="PV Power Balance"
                                        description="Controls PV power allocation in supported parallel systems"
                                        changed={
                                            !!changedSettings.pvPowerBalance
                                        }
                                    >
                                        <SelectControl
                                            value={
                                                settings.pvPowerBalance
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "pvPowerBalance",
                                                    value
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "",
                                                    label: "Not available"
                                                },
                                                {
                                                    value: "0",
                                                    label: "Maximum charge current"
                                                },
                                                {
                                                    value: "1",
                                                    label: "Charge current + load power"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                    <SettingRow
                                        label="Output Mode"
                                        description="Single, parallel or three-phase operation"
                                        changed={
                                            !!changedSettings.outputMode
                                        }
                                    >
                                        <SelectControl
                                            value={
                                                settings.outputMode
                                            }
                                            onChange={(value) =>
                                                updateSetting(
                                                    "outputMode",
                                                    value
                                                )
                                            }
                                            options={[
                                                {
                                                    value: "",
                                                    label: "Not available"
                                                },
                                                {
                                                    value: "Single",
                                                    label: "Single"
                                                },
                                                {
                                                    value: "Parallel",
                                                    label: "Parallel"
                                                },
                                                {
                                                    value: "Phase 1",
                                                    label: "Phase 1"
                                                },
                                                {
                                                    value: "Phase 2",
                                                    label: "Phase 2"
                                                },
                                                {
                                                    value: "Phase 3",
                                                    label: "Phase 3"
                                                }
                                            ]}
                                        />
                                    </SettingRow>

                                </div>

                            </section>

                            {/* ---------- Apply bar ---------- */}

                            <div className="settingsApplyBar">

                                <div className="applySummary">

                                    <div className="applyIcon">
                                        <FaCog />
                                    </div>

                                    <div>

                                        <strong>
                                            {changedCount === 0
                                                ? "No unsaved changes"
                                                : `${changedCount} setting${
                                                    changedCount === 1
                                                        ? ""
                                                        : "s"
                                                } changed`}
                                        </strong>

                                        <span>
                                            {changedCount === 0
                                                ? "The displayed values match the inverter."
                                                : "Changes will be sent to the inverter and verified."}
                                        </span>

                                    </div>

                                </div>

                                <div className="applyActions">

                                    {changedCount > 0 && (
                                        <button
                                            type="button"
                                            className="discardButton"
                                            onClick={resetChanges}
                                            disabled={saving}
                                        >
                                            Discard
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="applyButton"
                                        onClick={handleApplyChanges}
                                        disabled={
                                            saving ||
                                            changedCount === 0
                                        }
                                    >
                                        {saving ? (
                                            <>
                                                <FaSyncAlt className="spinIcon" />
                                                Applying...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave />
                                                Apply Changes
                                            </>
                                        )}
                                    </button>

                                </div>

                            </div>

                        </>

                    )}

                </div>

            </div>

        </div>
    );
}